import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useLanguage } from '../context/LanguageContext';
import { motion } from 'framer-motion';
import { Leaf, Mail, Lock, User, Building2, ArrowRight, Globe, Eye, EyeOff, Moon, Sun } from 'lucide-react';
import PasswordStrength from '../components/PasswordStrength';

const AuthPage = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [needsEmailVerification, setNeedsEmailVerification] = useState(false);
  const [pendingEmail, setPendingEmail] = useState('');
  const [resendInfo, setResendInfo] = useState(null);
  const [resendLoading, setResendLoading] = useState(false);
  // RGPD-1.4 — consent checkbox state (never pre-checked, signup only)
  const [consentAccepted, setConsentAccepted] = useState(false);

  const { login, register, resendVerificationEmail } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const { t, language, toggleLanguage } = useLanguage();

  const clearVerificationState = () => {
    setNeedsEmailVerification(false);
    setPendingEmail('');
    setResendInfo(null);
  };

  // After a successful registration, give the user a moment to read the
  // success banner, then auto-switch to login mode (email pre-filled).
  useEffect(() => {
    if (!successMessage) return;
    const switchTimer = setTimeout(() => {
      setIsLogin(true);
      setPassword('');
      setName('');
      setCompanyName('');
      setConsentAccepted(false);
    }, 1500);
    const clearTimer = setTimeout(() => {
      setSuccessMessage('');
    }, 6000);
    return () => {
      clearTimeout(switchTimer);
      clearTimeout(clearTimer);
    };
  }, [successMessage]);

  // Auto-clear the resend feedback banner after a few seconds.
  useEffect(() => {
    if (!resendInfo) return;
    const timer = setTimeout(() => setResendInfo(null), 6000);
    return () => clearTimeout(timer);
  }, [resendInfo]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (isLogin) {
        await login(email, password, rememberMe);
      } else {
        const trimmedCompanyName = companyName.trim();
        if (!trimmedCompanyName) {
          setError(t('auth.companyNameRequired'));
          setLoading(false);
          return;
        }
        await register(email, password, name, trimmedCompanyName, language, consentAccepted);
        setSuccessMessage(t('auth.registrationSuccess'));
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.code === 'EMAIL_NOT_VERIFIED') {
        setError('');
        setPendingEmail(email);
        setNeedsEmailVerification(true);
      } else {
        const message = typeof detail === 'string'
          ? detail
          : (detail?.message || t('errors.generic'));
        setError(message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (!pendingEmail) return;
    setResendLoading(true);
    setResendInfo(null);
    try {
      await resendVerificationEmail(pendingEmail);
      setResendInfo({ message: t('auth.emailResendSent'), type: 'success' });
    } catch (err) {
      const status = err.response?.status;
      if (status === 400) {
        setResendInfo({ message: t('auth.emailResendAlreadyVerified'), type: 'warning' });
      } else if (status === 429) {
        setResendInfo({ message: t('auth.emailResendRateLimit'), type: 'warning' });
      } else {
        setResendInfo({ message: t('auth.emailResendError'), type: 'error' });
      }
    } finally {
      setResendLoading(false);
    }
  };

  const handleBackToLogin = () => {
    clearVerificationState();
    setPassword('');
    setError('');
  };

  const handleToggleAuthMode = () => {
    setIsLogin(!isLogin);
    setError('');
    clearVerificationState();
  };

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    if (needsEmailVerification) {
      clearVerificationState();
    }
  };

  return (
    <div className={`min-h-screen flex ${isDark ? 'bg-slate-900' : 'bg-gradient-to-br from-blue-50 via-white to-green-50'}`}>
      {/* Left side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 items-center justify-center p-12 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600 via-blue-500 to-green-500 opacity-90"></div>
        <div className="absolute inset-0">
          <svg className="absolute bottom-0 left-0 w-full" viewBox="0 0 1440 320">
            <path fill="rgba(255,255,255,0.1)" d="M0,192L48,197.3C96,203,192,213,288,229.3C384,245,480,267,576,250.7C672,235,768,181,864,181.3C960,181,1056,235,1152,234.7C1248,235,1344,181,1392,154.7L1440,128L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"></path>
          </svg>
        </div>
        <div className="relative z-10 text-white max-w-lg">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="w-20 h-20 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center mb-8">
              <Leaf className="w-12 h-12" />
            </div>
            <h1 className="text-5xl font-bold mb-6">CarbonScope</h1>
            <p className="text-xl text-blue-100 mb-8">
              {t('auth.tagline')}
            </p>
          </motion.div>
        </div>
        {/* Brand logo — bottom left. Mon Plan Climat, inchange. Le logo du
            partenaire Carbon Fri n'est PAS ici : le degrade bleu-vert du hero
            rend son bleu moyen invisible (1,36-1,93:1) et imposerait une
            pastille blanche. Il vit au coin bas-droit du panneau du
            formulaire, en symetrie avec celui-ci. */}
        <div className="absolute bottom-8 left-12 z-10">
          <img
            src={language === 'fr' ? '/logo-plan-climat-fr.png' : '/logo-plan-climat-de.png'}
            alt={t('auth.brandLogoAlt')}
            className="h-12 w-auto opacity-90"
            data-testid="brand-logo"
          />
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex items-center justify-center p-8 pb-40 relative">
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md"
        >
          {/* Language & Theme toggles */}
          <div className="flex justify-end gap-2 mb-8">
            <button
              onClick={toggleLanguage}
              data-testid="auth-language-toggle"
              className={`p-3 rounded-xl transition-all ${
                isDark ? 'bg-slate-800 text-white hover:bg-slate-700' : 'bg-white text-gray-600 hover:bg-gray-100 shadow-sm'
              }`}
            >
              <Globe className="w-5 h-5" />
            </button>
            <button
              onClick={toggleTheme}
              data-testid="auth-theme-toggle"
              className={`p-3 rounded-xl transition-all ${
                isDark ? 'bg-slate-800 text-white hover:bg-slate-700' : 'bg-white text-gray-600 hover:bg-gray-100 shadow-sm'
              }`}
            >
              {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>
          </div>

          <div className={`p-8 rounded-2xl ${isDark ? 'bg-slate-800' : 'bg-white shadow-xl shadow-gray-200/50'}`}>
            <div className="text-center mb-8">
              <div className="lg:hidden w-16 h-16 rounded-xl bg-gradient-to-br from-blue-500 to-green-500 flex items-center justify-center mx-auto mb-4">
                <Leaf className="w-10 h-10 text-white" />
              </div>
              <h2 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {needsEmailVerification
                  ? t('auth.emailNotVerifiedTitle')
                  : (isLogin ? t('auth.welcomeBack') : t('auth.welcome'))}
              </h2>
              <p className={`mt-2 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                {needsEmailVerification
                  ? t('auth.emailNotVerifiedMessage')
                  : (isLogin ? t('auth.login') : t('auth.createAccount'))}
              </p>
            </div>

            {error && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-600 rounded-xl text-sm">
                {error}
              </div>
            )}

            {successMessage && (
              <div
                data-testid="auth-success-banner"
                className="mb-4 p-4 bg-green-50 border border-green-200 text-green-700 rounded-xl text-sm"
              >
                {successMessage}
              </div>
            )}

            {needsEmailVerification ? (
              <div data-testid="email-verification-block" className="space-y-4">
                <div className="flex flex-col items-center text-center">
                  <div className={`w-16 h-16 rounded-full flex items-center justify-center mb-4 ${isDark ? 'bg-amber-500/20' : 'bg-amber-50'}`}>
                    <Mail className={`w-8 h-8 ${isDark ? 'text-amber-400' : 'text-amber-500'}`} />
                  </div>
                  <p className={`text-sm ${isDark ? 'text-slate-300' : 'text-gray-600'}`}>
                    {t('auth.emailNotVerifiedHint').replace('{email}', pendingEmail || '')}
                  </p>
                </div>

                {resendInfo && (
                  <div
                    data-testid="resend-info-banner"
                    className={`p-4 rounded-xl text-sm border ${
                      resendInfo.type === 'success'
                        ? 'bg-green-50 border-green-200 text-green-700'
                        : resendInfo.type === 'warning'
                          ? 'bg-amber-50 border-amber-200 text-amber-700'
                          : 'bg-red-50 border-red-200 text-red-600'
                    }`}
                  >
                    {resendInfo.message}
                  </div>
                )}

                <button
                  type="button"
                  onClick={handleResend}
                  disabled={resendLoading}
                  data-testid="resend-verification-btn"
                  className="w-full py-3 px-4 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl font-medium flex items-center justify-center gap-2 hover:from-blue-600 hover:to-blue-700 transition-all disabled:opacity-50 shadow-lg shadow-blue-500/30"
                >
                  {resendLoading ? (
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  ) : (
                    <>
                      {t('auth.emailResendButton')}
                      <ArrowRight className="w-5 h-5" />
                    </>
                  )}
                </button>

                <button
                  type="button"
                  onClick={handleBackToLogin}
                  data-testid="back-to-login-btn"
                  className="w-full text-sm text-blue-500 font-medium hover:underline"
                >
                  {t('auth.emailResendBackToLogin')}
                </button>
              </div>
            ) : (
            <>
            <form onSubmit={handleSubmit} className="space-y-4">
              {!isLogin && (
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
                    {t('auth.name')}
                  </label>
                  <div className="relative">
                    <User className={`absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 ${isDark ? 'text-slate-400' : 'text-gray-400'}`} />
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      data-testid="register-name-input"
                      className={`w-full pl-12 pr-4 py-3 rounded-xl border transition-all focus:ring-2 focus:ring-blue-500 ${
                        isDark
                          ? 'bg-slate-700 border-slate-600 text-white placeholder-slate-400'
                          : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400'
                      }`}
                      placeholder={t('auth.name')}
                      required={!isLogin}
                    />
                  </div>
                </div>
              )}

              {!isLogin && (
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
                    {t('auth.companyNameLabel')}
                  </label>
                  <div className="relative">
                    <Building2 className={`absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 ${isDark ? 'text-slate-400' : 'text-gray-400'}`} />
                    <input
                      type="text"
                      value={companyName}
                      onChange={(e) => setCompanyName(e.target.value)}
                      data-testid="register-company-name-input"
                      className={`w-full pl-12 pr-4 py-3 rounded-xl border transition-all focus:ring-2 focus:ring-blue-500 ${
                        isDark
                          ? 'bg-slate-700 border-slate-600 text-white placeholder-slate-400'
                          : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400'
                      }`}
                      placeholder={t('auth.companyNamePlaceholder')}
                      required={!isLogin}
                    />
                  </div>
                  <p className={`mt-2 text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                    {t('auth.companyNameHelp')}
                  </p>
                </div>
              )}

              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
                  {t('auth.email')}
                </label>
                <div className="relative">
                  <Mail className={`absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 ${isDark ? 'text-slate-400' : 'text-gray-400'}`} />
                  <input
                    type="email"
                    value={email}
                    onChange={handleEmailChange}
                    data-testid="email-input"
                    className={`w-full pl-12 pr-4 py-3 rounded-xl border transition-all focus:ring-2 focus:ring-blue-500 ${
                      isDark 
                        ? 'bg-slate-700 border-slate-600 text-white placeholder-slate-400' 
                        : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400'
                    }`}
                    placeholder={t('auth.email')}
                    required
                  />
                </div>
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
                  {t('auth.password')}
                </label>
                <div className="relative">
                  <Lock className={`absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 ${isDark ? 'text-slate-400' : 'text-gray-400'}`} />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    data-testid="password-input"
                    className={`w-full pl-12 pr-12 py-3 rounded-xl border transition-all focus:ring-2 focus:ring-blue-500 ${
                      isDark 
                        ? 'bg-slate-700 border-slate-600 text-white placeholder-slate-400' 
                        : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400'
                    }`}
                    placeholder={t('auth.password')}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className={`absolute right-4 top-1/2 -translate-y-1/2 ${isDark ? 'text-slate-400 hover:text-slate-300' : 'text-gray-400 hover:text-gray-600'}`}
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                {!isLogin && <PasswordStrength password={password} />}
              </div>

              {/* Remember Me & Forgot Password (Login only) */}
              {isLogin && (
                <div className="flex items-center justify-between">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                      data-testid="remember-me-checkbox"
                      className="w-4 h-4 rounded border-gray-300 text-blue-500 focus:ring-blue-500"
                    />
                    <span className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                      {t('auth.rememberMe')}
                    </span>
                  </label>
                  <Link
                    to="/forgot-password"
                    data-testid="forgot-password-link"
                    className="text-sm text-blue-500 hover:underline"
                  >
                    {t('auth.forgotPassword')}
                  </Link>
                </div>
              )}

              {/* RGPD-1.4 — consent checkbox (signup only, never pre-checked) */}
              {!isLogin && (
                <div>
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={consentAccepted}
                      onChange={(e) => setConsentAccepted(e.target.checked)}
                      data-testid="register-consent-checkbox"
                      className="mt-1 w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                    />
                    <span className={`text-sm ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
                      {t('auth.consent.prefix')}
                      <Link
                        to="/terms"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-500 hover:underline"
                      >
                        {t('auth.consent.termsLink')}
                      </Link>
                      {t('auth.consent.middle')}
                      <Link
                        to="/privacy"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-500 hover:underline"
                      >
                        {t('auth.consent.privacyLink')}
                      </Link>
                      {t('auth.consent.suffix')}
                    </span>
                  </label>
                </div>
              )}

              <button
                type="submit"
                disabled={loading || (!isLogin && !consentAccepted)}
                data-testid="auth-submit-btn"
                className="w-full py-3 px-4 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl font-medium flex items-center justify-center gap-2 hover:from-blue-600 hover:to-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-blue-500/30"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                ) : (
                  <>
                    {isLogin ? t('auth.login') : t('auth.register')}
                    <ArrowRight className="w-5 h-5" />
                  </>
                )}
              </button>
            </form>

            <p className={`mt-6 text-center text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
              {isLogin ? t('auth.noAccount') : t('auth.hasAccount')}{' '}
              <button
                onClick={handleToggleAuthMode}
                data-testid="toggle-auth-mode"
                className="text-blue-500 font-medium hover:underline"
              >
                {isLogin ? t('auth.register') : t('auth.login')}
              </button>
            </p>
            </>
            )}
          </div>
        </motion.div>

        {/* Groupe partenaires — Etat de Fribourg puis Carbon Fri, ancre au coin
            bas-droit du panneau, en symetrie avec Mon Plan Climat au coin
            bas-gauche du hero (memes decalages, meme ligne de base : les deux
            panneaux ont toujours la meme hauteur). Alignes en bas (items-end).
            Etat de Fribourg — contraintes de la charte (Manuel Logotype 2010) :
            version principale (la compacte exige une validation SAMI) ; URL
            www.fr.ch incluse, obligatoire quand le logo figure seul sans
            Direction ni Service (2.7) ; positif noir, jamais recolore ni
            passe en negatif (negatif = validation SAMI, 2.9) ; SVG converti
            sans retouche depuis l'EPS officiel. h-20 = 81 px de large, soit
            ~21 mm, au-dessus du minimum de 18 mm (2.8). Marge de protection
            de 6 unites (2.15) = ~19 px a cette taille : gap-8 (32 px) vers
            Carbon Fri, px-5 (20 px) dans la pastille sombre.
            Pourquoi un ancrage absolu ne chevauche PAS la carte ici : le
            panneau reserve en bas un padding superieur au decalage du groupe
            plus sa hauteur (pire cas, mode sombre : 32 + 80 + 2x16 = 144 px,
            reserve 160 via pb-40). Ce padding appartient a la boite du
            panneau : quand le contenu deborde et que le panneau s'etire, il
            s'etire avec lui et reste toujours sous la carte. Marge minimale
            garantie : 48 px en clair, 16 px en sombre, quels que soient le
            viewport et le mode.
            Pastille blanche EN MODE SOMBRE SEULEMENT, commune aux deux logos :
            sur le fond clair, les logos sont nus (marine Carbon Fri 16,4:1,
            noir Etat de Fribourg sur fond quasi blanc) ; sur le fond sombre,
            le marine « CARBON FRI » tombe a 1,04:1 et le noir de l'Etat
            disparait. Carbon Fri impose deja la pastille : y loger l'Etat ne
            coute rien et reste conforme a sa charte (positif sur blanc). Les
            variantes sombres portent sur ce conteneur, jamais sur les images :
            aucun logo n'est recolore, filtre ni passe en negatif. */}
        <div className="absolute bottom-8 right-12 z-10 flex items-end gap-8 dark:bg-white dark:rounded-lg dark:px-5 dark:py-4">
          <img
            src="/logo-etat-fribourg.svg"
            alt={t('auth.partnerLogoEtatFribourgAlt')}
            className="h-20 w-auto"
            data-testid="partner-logo-etat-fribourg"
          />
          <img
            src="/logo-carbonfri.png"
            alt={t('auth.partnerLogoCarbonfriAlt')}
            className="h-12 w-auto"
            data-testid="partner-logo-carbonfri"
          />
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
